import React, { useState, useMemo } from "react";
import { View, ScrollView, TextInput, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { getTermFieldMetadata, TermFieldGroups } from "@/types/schemas/terms";

export type TermPickerProps = {
  selectedFieldKeys?: string[];
  onPick?: (fieldKey: string) => void;
  onMultiPick?: (fieldKeys: string[]) => void;
  onCancel?: () => void;
  multiple?: boolean;
  excludeFieldKeys?: string[];
};

export function TermPicker({
  selectedFieldKeys = [],
  onPick,
  onMultiPick,
  onCancel,
  multiple = false,
  excludeFieldKeys = [],
}: TermPickerProps) {
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const [selectedFields, setSelectedFields] = useState<string[]>(selectedFieldKeys);

  // Get all field metadata
  const TERM_FIELDS = useMemo(() => getTermFieldMetadata(), []);

  // Get all available field keys (not excluded)
  const availableFieldKeys = useMemo(() => {
    const allKeys = Object.values(TermFieldGroups).flat();
    return allKeys.filter((key) => !excludeFieldKeys.includes(key));
  }, [excludeFieldKeys]);

  // Filter fields by search
  const filteredFieldKeys = useMemo(() => {
    if (!search) return availableFieldKeys;
    const searchLower = search.toLowerCase();
    return availableFieldKeys.filter((key) => {
      const field = TERM_FIELDS[key];
      if (!field) return false;
      return (
        field.label.toLowerCase().includes(searchLower) ||
        field.description.toLowerCase().includes(searchLower) ||
        key.toLowerCase().includes(searchLower)
      );
    });
  }, [availableFieldKeys, search, TERM_FIELDS]);

  const handleFieldSelect = (fieldKey: string) => {
    if (multiple) {
      const isSelected = selectedFields.includes(fieldKey);
      if (isSelected) {
        setSelectedFields(selectedFields.filter((k) => k !== fieldKey));
      } else {
        setSelectedFields([...selectedFields, fieldKey]);
      }
    } else {
      onPick?.(fieldKey);
    }
  };

  const handleConfirmMultiple = () => {
    onMultiPick?.(selectedFields);
  };

  const renderField = (fieldKey: string) => {
    const field = TERM_FIELDS[fieldKey];
    if (!field) return null;

    const isSelected = selectedFields.includes(fieldKey);

    return (
      <Pressable
        key={fieldKey}
        testID={`term-field-option-${fieldKey}`}
        style={[
          styles.fieldItem,
          { borderBottomColor: colors.border },
          multiple && isSelected && { backgroundColor: colors.primary + "10" },
        ]}
        onPress={() => handleFieldSelect(fieldKey)}
      >
        <View style={styles.fieldContent}>
          <View style={styles.fieldInfo}>
            <ThemedText type="defaultSemiBold">{field.description || field.label}</ThemedText>
            <ThemedText style={styles.fieldDetail}>
              {field.label} • {field.type}
            </ThemedText>
          </View>
          {multiple && (
            <MaterialCommunityIcons
              name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
              size={24}
              color={isSelected ? colors.primary : colors.text}
            />
          )}
        </View>
      </Pressable>
    );
  };

  return (
    <View style={styles.container} testID="term-picker">
      <ThemedText type="subtitle">{multiple ? "Add Fields" : "Add Field"}</ThemedText>

      <TextInput
        testID="term-search-input"
        style={[
          styles.searchInput,
          {
            borderColor: colors.border,
            color: colors.text,
            backgroundColor: colors.backgroundSecondary,
          },
        ]}
        placeholder="Search by description or name..."
        placeholderTextColor={colors.placeholder}
        value={search}
        onChangeText={setSearch}
        autoFocus
      />

      <ScrollView style={styles.scrollView}>
        {filteredFieldKeys.length > 0 ? (
          filteredFieldKeys.map(renderField)
        ) : (
          <ThemedText style={styles.message}>{search ? "No fields found" : "No fields available"}</ThemedText>
        )}
      </ScrollView>

      <View style={styles.footer}>
        {multiple && (
          <>
            <ThemedText style={styles.selectedCount}>{selectedFields.length} selected</ThemedText>
            <View style={styles.footerButtons}>
              <Button
                text="Cancel"
                style={[styles.cancelButton, { borderColor: colors.border }]}
                textStyle={{ color: colors.text }}
                onPress={onCancel}
              />
              <Button
                text="Confirm"
                style={styles.confirmButton}
                onPress={handleConfirmMultiple}
                enabled={selectedFields.length > 0}
              />
            </View>
          </>
        )}
        {!multiple && onCancel && (
          <Button
            text="Cancel"
            style={[styles.cancelButton, { borderColor: colors.border }]}
            textStyle={{ color: colors.text }}
            onPress={onCancel}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    gap: 8,
  },
  searchInput: {
    height: 36,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  scrollView: {
    maxHeight: 350,
  },
  message: {
    opacity: 0.7,
    alignSelf: "center",
    marginVertical: 20,
  },
  fieldItem: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    backgroundColor: "transparent",
  },
  fieldContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  fieldInfo: {
    flex: 1,
    gap: 4,
  },
  fieldDetail: {
    fontSize: 12,
    opacity: 0.6,
  },
  footer: {
    marginTop: 8,
  },
  selectedCount: {
    opacity: 0.7,
    marginBottom: 8,
  },
  footerButtons: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
  },
  cancelButton: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 32,
    paddingHorizontal: 16,
  },
});
